import { Field, Input, Select } from '@dayon/design-system';

/**
 * The commission terms, as a form.
 *
 * Shared by "New affiliate" and "Change commission model" because they collect
 * exactly the same five values and the coherence rule between them is one rule:
 * `affiliate_plan_model_coherent` requires a rate for REVSHARE, an amount for
 * CPA and both for HYBRID. Two copies of these fields would be two places for
 * that rule to be half-remembered.
 *
 * Money is entered in PESOS and sent as CENTAVOS. The conversion happens once,
 * in `toCentavos` below, and never through a float: `parseFloat('850.10') * 100`
 * is 85009.99999999999, and a bounty that pays ₱850.09 because of binary
 * rounding is exactly the class of defect the bigint-centavos rule exists for.
 */

export interface PlanTermsState {
  model: 'REVSHARE' | 'CPA' | 'HYBRID';
  /** Percent, as typed. "30" or "27.5". */
  revsharePct: string;
  /** Pesos, as typed. */
  cpaPesos: string;
  cpaHoldDays: string;
  cpaMinDepositPesos: string;
}

export const EMPTY_PLAN: PlanTermsState = {
  model: 'REVSHARE',
  revsharePct: '',
  cpaPesos: '',
  cpaHoldDays: '30',
  cpaMinDepositPesos: '',
};

const MODEL_OPTIONS: readonly { value: PlanTermsState['model']; label: string }[] = [
  { value: 'REVSHARE', label: 'Revenue share — a percentage of net GGR, lifetime' },
  { value: 'CPA', label: 'Cost per acquisition — a bounty per qualifying first deposit' },
  { value: 'HYBRID', label: 'Hybrid — a bounty plus a revenue share' },
];

/**
 * Pesos as typed → centavos as an integer string, or null for blank.
 *
 * Digits only, with at most two decimal places, assembled as integers so no
 * float ever touches the amount. Returns `undefined` for input that is not a
 * peso amount at all, which the caller reports inline.
 */
export function toCentavos(input: string): string | null | undefined {
  const text = input.trim();
  if (text === '') return null;
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (!m) return undefined;
  const cents = (m[2] ?? '').padEnd(2, '0');
  return (BigInt(m[1]!) * 100n + BigInt(cents)).toString();
}

/** Percent as typed → basis points, or null for blank; undefined if unreadable. */
export function toBasisPoints(input: string): number | null | undefined {
  const text = input.trim();
  if (text === '') return null;
  const m = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(text);
  if (!m) return undefined;
  const bp = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  if (bp > 10000) return undefined;
  return bp;
}

/**
 * Turn the form state into the request body, or into per-field messages.
 *
 * One function so the two dialogs cannot validate the same terms differently.
 * Every message names the field and the rule; none of them says "invalid".
 */
export function planTermsToBody(state: PlanTermsState): {
  body?: Record<string, unknown>;
  fields?: Record<string, string>;
} {
  const fields: Record<string, string> = {};
  const needsRate = state.model === 'REVSHARE' || state.model === 'HYBRID';
  const needsCpa = state.model === 'CPA' || state.model === 'HYBRID';

  const revshareBp = toBasisPoints(state.revsharePct);
  if (revshareBp === undefined) {
    fields['revshareBp'] = 'Enter a percentage between 0 and 100, with at most two decimals.';
  } else if (needsRate && revshareBp === null) {
    fields['revshareBp'] = 'A revenue-share plan needs a rate.';
  }

  const cpaAmount = toCentavos(state.cpaPesos);
  if (cpaAmount === undefined) {
    fields['cpaAmount'] = 'Enter a peso amount, digits with at most two decimal places.';
  } else if (needsCpa && (cpaAmount === null || BigInt(cpaAmount) <= 0n)) {
    fields['cpaAmount'] = 'A CPA plan needs a bounty of more than nothing.';
  }

  const cpaMinDeposit = toCentavos(state.cpaMinDepositPesos);
  if (cpaMinDeposit === undefined) {
    fields['cpaMinDeposit'] = 'Enter a peso amount, digits with at most two decimal places.';
  }

  const hold = Number(state.cpaHoldDays);
  if (!Number.isInteger(hold) || hold < 0 || hold > 365) {
    fields['cpaHoldDays'] = 'A hold is a whole number of days between 0 and 365.';
  }

  if (Object.keys(fields).length > 0) return { fields };

  return {
    body: {
      model: state.model,
      // Sent as null rather than omitted where the model does not use them, so
      // switching HYBRID → REVSHARE actually CLEARS the bounty instead of
      // carrying it forward into a plan that does not pay it.
      revshareBp: needsRate ? revshareBp : null,
      cpaAmount: needsCpa ? cpaAmount : null,
      cpaHoldDays: hold,
      cpaMinDeposit: needsCpa ? cpaMinDeposit : null,
    },
  };
}

export function PlanFields({
  state,
  onChange,
  fields,
}: {
  state: PlanTermsState;
  onChange: (next: PlanTermsState) => void;
  fields: Record<string, string>;
}) {
  const set = <K extends keyof PlanTermsState>(key: K, value: PlanTermsState[K]) =>
    onChange({ ...state, [key]: value });

  const needsRate = state.model === 'REVSHARE' || state.model === 'HYBRID';
  const needsCpa = state.model === 'CPA' || state.model === 'HYBRID';

  return (
    <>
      <Field label="Commission model">
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={state.model}
            onChange={(e) => set('model', e.target.value as PlanTermsState['model'])}
          >
            {MODEL_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {/* Only the fields the chosen model actually uses are shown. A rate box
          on a CPA plan is a value an operator will fill and the plan will
          never pay. */}
      {needsRate && (
        <Field
          label="Revenue share (%)"
          error={fields['revshareBp']}
          hint="Of net GGR, for the lifetime of every player attributed while this plan is live."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              inputMode="decimal"
              value={state.revsharePct}
              onChange={(e) => set('revsharePct', e.target.value)}
              placeholder="30"
            />
          )}
        </Field>
      )}

      {needsCpa && (
        <>
          <Field
            label="Bounty per first deposit (₱)"
            error={fields['cpaAmount']}
            hint="Paid once per referred player, ever."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                inputMode="decimal"
                value={state.cpaPesos}
                onChange={(e) => set('cpaPesos', e.target.value)}
                placeholder="850"
              />
            )}
          </Field>

          <Field
            label="Hold (days)"
            error={fields['cpaHoldDays']}
            hint="A bounty is held this many days, then becomes payable once its player is verified and not suspended. A refused verification voids it while it is held."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                inputMode="numeric"
                value={state.cpaHoldDays}
                onChange={(e) => set('cpaHoldDays', e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Qualifying minimum deposit (₱)"
            error={fields['cpaMinDeposit']}
            hint="Leave blank for no floor. A first deposit below this earns no bounty — a rule none of the plans the platform started with carries."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                inputMode="decimal"
                value={state.cpaMinDepositPesos}
                onChange={(e) => set('cpaMinDepositPesos', e.target.value)}
                placeholder="500"
              />
            )}
          </Field>
        </>
      )}
    </>
  );
}
